// System roles and capabilities for the EcoIntel platform.
// Roles determine access level; subscription status determines whether access is active.

export const ROLES = {
  ADMIN: 'admin',
  SUPERUSER: 'superuser',
  USER: 'user',
  DEMO: 'demo',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SUPERUSER]: 'Superuser',
  [ROLES.USER]: 'User',
  [ROLES.DEMO]: 'Demo',
};

export const ROLE_DESCRIPTIONS = {
  [ROLES.ADMIN]: 'Full access to all properties, users, and system settings',
  [ROLES.SUPERUSER]: 'Multiple properties with ability to manage associated users',
  [ROLES.USER]: 'Access to assigned properties only',
  [ROLES.DEMO]: 'Read-only access to assigned showcase properties',
};

export const ROLE_BADGE_STYLES = {
  [ROLES.ADMIN]: 'bg-emerald-100 text-emerald-700',
  [ROLES.SUPERUSER]: 'bg-purple-100 text-purple-700',
  [ROLES.USER]: 'bg-slate-100 text-slate-600',
  [ROLES.DEMO]: 'bg-amber-100 text-amber-700',
};

// Plan tiers (Phase 0). The freemium model is intentionally simple:
// every user starts on FREE; paying customers are PAID. Reports are purchased
// separately as add-on subscriptions per package (see featureGates.js).
export const PLAN_TIERS = {
  FREE: 'free',
  PAID: 'paid',
};

export const PLAN_TIER_LABELS = {
  [PLAN_TIERS.FREE]: 'Free',
  [PLAN_TIERS.PAID]: 'Paid',
};

export const PLAN_TIER_BADGE_STYLES = {
  [PLAN_TIERS.FREE]: 'bg-slate-100 text-slate-600',
  [PLAN_TIERS.PAID]: 'bg-emerald-100 text-emerald-700',
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  // Set by the Stripe `customer.subscription.updated` webhook (and
  // optimistically by /cancel-subscription + /admin/cancel-user-
  // subscription) when `cancel_at_period_end === true`. The user keeps
  // paid access until the end of the current Stripe billing period;
  // `hasActiveAccess` + `resolvePlanTier` below treat this state the
  // same as ACTIVE for that reason. Flips to CANCELLED via the
  // `customer.subscription.deleted` webhook at period end.
  CANCELLING: 'cancelling',
  PAST_DUE: 'past_due',
  CANCELLED: 'cancelled',
  NONE: 'none',
};

export const SUBSCRIPTION_LABELS = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'Active',
  [SUBSCRIPTION_STATUS.CANCELLING]: 'Cancelling',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'Past Due',
  [SUBSCRIPTION_STATUS.CANCELLED]: 'Cancelled',
  [SUBSCRIPTION_STATUS.NONE]: 'No Subscription',
};

export const SUBSCRIPTION_BADGE_STYLES = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'bg-emerald-100 text-emerald-700',
  // Cancelling is mid-state: still paid (so emerald-ish) but with an
  // amber edge to make the "ending soon" lifecycle visible at a glance.
  [SUBSCRIPTION_STATUS.CANCELLING]: 'bg-amber-100 text-amber-700',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'bg-amber-100 text-amber-700',
  [SUBSCRIPTION_STATUS.CANCELLED]: 'bg-red-100 text-red-700',
  [SUBSCRIPTION_STATUS.NONE]: 'bg-slate-100 text-slate-600',
};

// Capability checks
export function canSeeAllProperties(role) {
  return role === ROLES.ADMIN;
}

export function canImportProperties(role) {
  return role === ROLES.ADMIN;
}

export function canManageAllUsers(role) {
  return role === ROLES.ADMIN;
}

export function canManageAssociatedUsers(role) {
  return role === ROLES.SUPERUSER || role === ROLES.ADMIN;
}

export function canUploadProperty(role) {
  return role === ROLES.ADMIN || role === ROLES.SUPERUSER || role === ROLES.USER;
}

export function canEditParcelData(role) {
  return role !== ROLES.DEMO;
}

export function canDeleteProperty(role) {
  return role === ROLES.ADMIN;
}

export function isReadOnly(role) {
  return role === ROLES.DEMO;
}

// Framework on/off is a per-property admin action (stand-in: farm ownerUid).
// Platform Admin may edit any property. Superuser may edit assigned properties.
// A grantable per-property "property admin" role comes later; portfolio
// settings will override this property-level choice when that ships.
export function canEditPropertyFrameworks({ role, uid, farmIds, ownerUid, farmId }) {
  if (!role || role === ROLES.DEMO) return false;
  if (role === ROLES.ADMIN) return true;
  if (role === ROLES.SUPERUSER) {
    const ids = Array.isArray(farmIds) ? farmIds : [];
    return !!(farmId && ids.includes(farmId));
  }
  return !!(ownerUid && uid && ownerUid === uid);
}

export function hasActiveAccess(role, subscriptionStatus, subscriptionOverride) {
  if (role === ROLES.ADMIN) return true;
  if (role === ROLES.DEMO) return true;
  if (subscriptionOverride) return true;
  // `cancelling` is treated as active until the Stripe billing period
  // actually ends — at which point the `customer.subscription.deleted`
  // webhook flips the status to `cancelled` and access drops.
  return subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE
    || subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLING;
}

// Phase 0: resolved plan tier for a user record. Centralises the rules for
// when a user has effective paid-tier access (Stripe sub, admin override, or
// admin role). Use this when deciding whether to surface paid features.
// Reports are a SEPARATE concern; see hasReportPackageAccess in featureGates.js.
export function resolvePlanTier(userData) {
  if (!userData) return PLAN_TIERS.FREE;
  if (userData.role === ROLES.ADMIN) return PLAN_TIERS.PAID;
  if (userData.subscriptionOverride === true) return PLAN_TIERS.PAID;
  if (userData.planTier === PLAN_TIERS.PAID) return PLAN_TIERS.PAID;
  // Back-compat with Founding Associate users created before planTier existed.
  // `cancelling` is treated the same as `active` here — the user has paid
  // through the end of the current billing period and should retain all
  // paid-tier features until the `customer.subscription.deleted` webhook
  // fires and flips the status to `cancelled`.
  if (userData.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) return PLAN_TIERS.PAID;
  if (userData.subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLING) return PLAN_TIERS.PAID;
  return PLAN_TIERS.FREE;
}

export function isPaidTier(userData) {
  return resolvePlanTier(userData) === PLAN_TIERS.PAID;
}

/** True when self-service account deletion must wait for cancel-at-period-end. */
export function mustCancelSubscriptionBeforeAccountDelete(userData) {
  if (!userData || userData.subscriptionOverride === true) return false;
  if (!userData.stripeSubscriptionId) return false;
  const status = userData.subscriptionStatus || SUBSCRIPTION_STATUS.NONE;
  return status === SUBSCRIPTION_STATUS.ACTIVE || status === SUBSCRIPTION_STATUS.PAST_DUE;
}

// Admins can assign any role including another admin (e.g. for platform operators).
export const ALL_ASSIGNABLE_ROLES = [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.USER, ROLES.DEMO];
